// apps/patient/src/components/messages/MessageComposer.tsx

import { useState, useRef } from 'react';
import {
  Send,
  Paperclip,
  X,
  AlertCircle,
  Image,
  File
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { patientApiService as apiService } from '../../services/api';

export default function MessageComposer({
  threadId,
  onSend,
  onCancel,
  disabled = false,
  replyToMessage = null,
  placeholder = "Type your message...",
  isPatient = true
}) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleSend = async () => {
    if (!message.trim() && attachments.length === 0) return;

    setError(null);

    try {
      const messageData = {
        body: message.trim(),
        parent_message_id: replyToMessage?.id,
        contains_attachments: attachments.length > 0
      };

      // Send message
      const response = await onSend(messageData);

      // Upload attachments if any
      if (attachments.length > 0 && response) {
        setUploading(true);

        try {
          const uploadPromises = attachments.map(file =>
            apiService.uploadMessageAttachment(file, response.id, threadId)
          );

          const uploadResults = await Promise.all(uploadPromises);

          await apiService.updateMessage(response.id, {
            attachments: uploadResults.map(attachment => ({
              id: attachment.id,
              filename: attachment.filename || attachment.file_name,
              url: attachment.file_url || attachment.url,
              mime_type: attachment.mime_type,
              size: attachment.file_size
            })),
            attachment_ids: uploadResults.map(a => a.id),
            contains_attachments: true
          });

          // Update message with attachment info if callback provided
          if (response.onAttachmentsUploaded) {
            response.onAttachmentsUploaded(uploadResults);
          }
        } catch (uploadError) {
          console.error('Error uploading attachments:', uploadError);
          setError('Some attachments failed to upload');
        } finally {
          setUploading(false);
        }
      }

      // Clear form
      setMessage('');
      setAttachments([]);
      if (onCancel) onCancel(); // Clear reply state

    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setAttachments(prev => [...prev, ...files]);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return Image;
    return File;
  };

  return (
    <div className="border-t bg-white">
      {/* Error display */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200">
          <div className="flex items-center gap-2 text-red-800 text-sm">
            <AlertCircle className="h-4 w-4" />
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-auto"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Reply indicator */}
      {replyToMessage && (
        <div className="px-4 py-2 bg-gray-50 border-b flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Replying to: <span className="font-medium">{replyToMessage.sender_name}</span>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Attachments preview */}
      {attachments.length > 0 && (
        <div className="px-4 py-2 border-b bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {attachments.map((file, index) => {
              const FileIcon = getFileIcon(file);
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-white border rounded-lg"
                >
                  <FileIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700 max-w-[150px] truncate">
                    {file.name}
                  </span>
                  <button
                    onClick={() => removeAttachment(index)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Message input area */}
      <div className="flex items-end gap-2 p-4">
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            disabled={disabled || uploading}
            rows={1}
            className={cn(
              "w-full px-3 py-2 border border-gray-300 rounded-lg resize-none",
              "focus:ring-2 focus:ring-primary focus:border-transparent",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              "max-h-32"
            )}
            style={{
              minHeight: '40px',
              height: 'auto'
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement; // Type assertion
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 128) + 'px';
            }}
          />
        </div>

        {/* Attachment button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || uploading}
          className={cn(
            "p-2 rounded-lg transition-colors",
            "text-gray-500 hover:text-gray-700 hover:bg-gray-100",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          title="Attach files"
        >
          <Paperclip className="h-5 w-5" />
        </button>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={disabled || uploading || (!message.trim() && attachments.length === 0)}
          className={cn(
            "p-2 rounded-lg transition-colors",
            "bg-primary text-white hover:bg-primary/90",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
          title="Send message"
        >
          {uploading ? (
            <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </button>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept="image/*,.pdf,.doc,.docx,.txt"
        />
      </div>
    </div>
  );
}